import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Button, Form, FormControl, InputGroup, Modal } from 'react-bootstrap';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { useDispatch } from 'react-redux';

import fr from 'date-fns/locale/fr';
import { DateTime, Info, Settings, WeekdayNumbers } from 'luxon';

import { capitalizeWord } from '../../../../_helpers/stringHelper';
import { defaultTheme } from '../../../../assets/css/defaultTheme';
import Up from '../../../../assets/svg/Up';
import { updateTeamMemberAgenda } from '../../../myTeam/actions';
import { ITeamMember } from '../../../myTeam/reducers';
import { setAgendaRange } from '../../actions';
import { AgendaRangeType } from '../../enums';
import { AgendaRange, HourAndMinute } from '../../reducers';
import { AgendaRangeMinute } from '../../types';

registerLocale('fr', fr);
setDefaultLocale('fr');

Settings.defaultLocale = 'fr';

interface ModalAgendaPreferencesProps {
  rangeData: AgendaRange;
  show: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  team: ITeamMember[];
}

const ModalAgendaPreferences = (props: ModalAgendaPreferencesProps) => {
  const agendaRangeMinuteOptions: AgendaRangeMinute[] = [15, 30, 60];
  const dispatch = useDispatch();
  const [rangeData, setRangeData] = useState<AgendaRange>({
    type: props.rangeData.type,
    dayFrom: props.rangeData.dayFrom,
    dayTo: props.rangeData.dayTo,
    timeFrom: props.rangeData.timeFrom,
    timeTo: props.rangeData.timeTo,
    range: props.rangeData.range
  });
  const [searchInput, setSearchInput] = useState('');
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setRangeData({
      type: props.rangeData.type,
      dayFrom: props.rangeData.dayFrom,
      dayTo: props.rangeData.dayTo,
      timeFrom: props.rangeData.timeFrom,
      timeTo: props.rangeData.timeTo,
      range: props.rangeData.range
    });
  }, [props.rangeData]);

  const adjustIndexForWeekdayNumbers = (i: number) => {
    return (i + 1) % 7 === 0 ? 7 : (i + 1) % 7;
  };
  const convertHourAndMinuteToDate = (hourAndMinute: HourAndMinute): Date => {
    const { hour, minute } = hourAndMinute;
    const currentDate = new Date();
    currentDate.setHours(hour);
    currentDate.setMinutes(minute);
    return currentDate;
  };
  const handleChange = () => {
    dispatch(setAgendaRange(rangeData));
    handleClose();
  };
  const handleCheckChange = (index: number) => {
    const teamMember = props.team[index];
    const updatedMember = { ...teamMember, isChecked: !teamMember.isChecked };
    dispatch(updateTeamMemberAgenda(index, updatedMember));
  };
  const handleClose = () => {
    props.setShow(false);
  };
  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };
  const handleSelectAllChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      props.team.forEach((member, index) => {
        const updatedMember = { ...member, isChecked: true };
        dispatch(updateTeamMemberAgenda(index, updatedMember));
      });
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <div className="d-flex flex-column justify-content-center m-4">
        <div className="mb-1 d-flex flex-row justify-content-between">
          <div className="d-flex flex-row justify-content-start gap-2 mb-0 pb-0">
            <h4 className="short-agenda-modal mt-3 pt-2 mb-0">
              <p className="fw-bold" style={{ fontSize: '24px' }}>
                Préférences :{' '}
              </p>
            </h4>
          </div>
          <Button variant="none" onClick={handleClose}>
            <Up color={defaultTheme.palette.primary.dark} />
          </Button>
        </div>
        <div className="d-flex flex-column d-flex justify-content-between">
          <h5 className="short-agenda-modal fw-light">Collaborateurs</h5>
          <small className="font-nunito text-muted mb-0 mt-0 p-0">Affichez les plannings qui vous intéressent</small>
          <InputGroup className="mt-3 mb-3">
            <Button className="search-button-navbar" id="search" type="submit">
              <i className="bi bi-search"></i>
            </Button>
            <FormControl
              className="search-input-navbar"
              placeholder="Rechercher"
              aria-label="search"
              aria-describedby="search"
              value={searchInput}
              onChange={handleSearchInputChange}
            />
          </InputGroup>
          <Form.Check
            type="checkbox"
            label={`Sélectionner tous les collaborateurs`}
            checked={selectAll}
            onChange={handleSelectAllChange}
          />
          <div id="collaboratorsList">
            {props.team
              .filter((member) => member.user.firstname.toLowerCase().includes(searchInput.toLowerCase()))
              .map((member, index) => {
                return (
                  <Form.Check
                    key={index}
                    type="checkbox"
                    checked={member.isChecked}
                    label={member.user.firstname + ' ' + capitalizeWord(member.user.lastname)}
                    onChange={() => handleCheckChange(index)}
                  />
                );
              })}
          </div>
        </div>
        <div className="d-flex flex-column d-flex justify-content-between">
          <h5 className="short-agenda-modal fw-light mt-3">Calendrier</h5>
          <small className="font-nunito text-muted mb-2 mt-0 p-0">
            Sélectionnez les plages horaires qui vous correspondent.
          </small>
          <div className="d-flex flex-column d-flex justify-content-start">
            {/*<Form.Check*/}
            {/*  checked={rangeData.type === AgendaRangeType.DAY}*/}
            {/*  className="radio-order"*/}
            {/*  name="radio-appointment"*/}
            {/*  type="radio"*/}
            {/*  label="Jour"*/}
            {/*  id="orderByDay"*/}
            {/*  onChange={() => setRangeData({ ...rangeData, type: AgendaRangeType.DAY })}*/}
            {/*/>*/}
            <Form.Check
              checked={rangeData.type === AgendaRangeType.WEEK}
              className="radio-order"
              name="radio-appointment"
              type="radio"
              label="Semaine"
              id="orderByWeek"
              onChange={() => setRangeData({ ...rangeData, type: AgendaRangeType.WEEK })}
            />
            <Form.Check
              checked={rangeData.type === AgendaRangeType.MONTH}
              className="radio-order"
              name="radio-appointment"
              type="radio"
              label="Mois"
              id="orderByMonth"
              onChange={() => setRangeData({ ...rangeData, type: AgendaRangeType.MONTH })}
            />
            <Form.Check
              checked={rangeData.type === AgendaRangeType.CUSTOM}
              className="radio-order"
              name="radio-appointment"
              type="radio"
              label="Personnalisé"
              id="orderByCustom"
              onChange={() => setRangeData({ ...rangeData, type: AgendaRangeType.CUSTOM })}
            />
            {rangeData.type === AgendaRangeType.CUSTOM && (
              <>
                <div className="d-flex flex-row d-flex justify-content-between align-items-center align-middle gap-2 mt-1">
                  <select
                    className="form-select"
                    id="orderByCustomDayFrom"
                    onChange={(e) =>
                      setRangeData({
                        ...rangeData,
                        dayFrom: (e.target.selectedIndex + 1) as WeekdayNumbers
                      })
                    }
                    defaultValue={rangeData.dayFrom}
                  >
                    {Info.weekdays().map((day, i) => {
                      return (
                        <option key={day} value={adjustIndexForWeekdayNumbers(i)}>
                          Du {day}
                        </option>
                      );
                    })}
                  </select>
                  <select
                    className="form-select"
                    id="orderByCustomDayTo"
                    onChange={(e) =>
                      setRangeData({
                        ...rangeData,
                        dayTo: (e.target.selectedIndex + 1) as WeekdayNumbers
                      })
                    }
                    defaultValue={rangeData.dayTo}
                  >
                    {Info.weekdays().map((day, i) => {
                      return (
                        <option key={day} value={adjustIndexForWeekdayNumbers(i)}>
                          Au {day}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="d-flex flex-row d-flex justify-content-start align-items-center align-middle gap-2 mt-1">
                  <p className="m-0 p-0">de</p>
                  <DatePicker
                    className="form-select"
                    selected={convertHourAndMinuteToDate(rangeData.timeFrom)}
                    onChange={(date) =>
                      date
                        ? setRangeData({
                            ...rangeData,
                            timeFrom: {
                              hour: DateTime.fromJSDate(date).hour,
                              minute: DateTime.fromJSDate(date).minute
                            }
                          })
                        : null
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={rangeData.range}
                    timeCaption="Heure"
                    dateFormat="HH:mm"
                  />
                  <p className="m-0 p-0">a</p>
                  <DatePicker
                    className="form-select"
                    selected={convertHourAndMinuteToDate(rangeData.timeTo)}
                    onChange={(date) =>
                      date
                        ? setRangeData({
                            ...rangeData,
                            timeTo: {
                              hour: DateTime.fromJSDate(date).hour,
                              minute: DateTime.fromJSDate(date).minute
                            }
                          })
                        : null
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={rangeData.range}
                    timeCaption="Heure"
                    dateFormat="HH:mm"
                  />
                </div>
                <div className="d-flex flex-row d-flex justify-content-start align-items-center align-middle gap-2 mt-1">
                  <p className="m-0 p-0">afficher</p>
                  <select
                    className="form-select form-select-sm w-50"
                    id="showPerLines"
                    onChange={(e) =>
                      setRangeData({
                        ...rangeData,
                        range: parseInt(e.target.value) as AgendaRangeMinute
                      })
                    }
                  >
                    {agendaRangeMinuteOptions.map((value) => (
                      <option key={value} selected={value === rangeData.range}>
                        {value} minutes
                      </option>
                    ))}
                  </select>
                  <p className="m-0 p-0">par ligne</p>
                </div>
              </>
            )}
          </div>
          <Button className="mx-auto mt-3" variant="primary" role="button" onClick={() => handleChange()}>
            Enregistrer les préférences
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ModalAgendaPreferences;
